import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';

import type { RailFormInput } from '../../schemas/block-form-schema';
import { CollectionField } from '../collection-field';
import { RailDisplayFields } from '../rail/rail-display-fields';
import { RailHeaderFields } from '../rail/rail-header-fields';
import { RailProductsList } from '../rail/rail-products-list';
import { RailSourceFields } from '../rail/rail-source-fields';

export interface RailEditorProps {
  /** Null for a shared rail: hand-picked only, publication varies per store. */
  storeId: string | null;
  /** Publication of the saved products in this store; see RailProductsList. */
  publication?: ReadonlyMap<string, boolean>;
}

/** The rail form body: products (source, collection or list), display and the optional header. */
export function RailEditor({ storeId, publication }: RailEditorProps) {
  const { t } = useTranslation('content');
  const form = useFormContext<RailFormInput>();
  const sourceType = useWatch({ control: form.control, name: 'sourceType' });

  return (
    <>
      <FormSection
        title={t('sections.railSource')}
        description={t('sections.railSourceDescription')}
      >
        <RailSourceFields storeId={storeId} />
        {sourceType === 'collection' && storeId !== null ? (
          <FormField
            control={form.control}
            name="collectionId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('rail.collection')}</FormLabel>
                <CollectionField storeId={storeId} value={field.value} onChange={field.onChange} />
                <FormDescription>{t('rail.collectionHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <RailProductsList storeId={storeId} publication={publication} />
        )}
      </FormSection>
      <FormSection title={t('sections.display')} description={t('sections.displayDescription')}>
        <RailDisplayFields />
      </FormSection>
      <FormSection
        title={t('sections.railHeader')}
        description={t('sections.railHeaderDescription')}
      >
        <RailHeaderFields storeId={storeId} />
      </FormSection>
    </>
  );
}
